import { Trans } from '@lingui/react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { FormikActions, FormSpacing } from 'components/Form';
import useForm from 'hooks/useForm';
import { i18n } from 'i18n';
import { InspectionPointMutation } from 'providers/api';
import { useEffect } from 'react';
import { UseMutationResult } from 'react-query';
import { Weaken } from 'utils';
import { object, SchemaOf, string } from 'yup';
import { SPOOL_NAME_MAX_LENGTH } from '../SpoolForm/spoolFormUtils';

const InspectionPointSchema: SchemaOf<InspectionPointMutation> = object().shape({
  name: string()
    .max(SPOOL_NAME_MAX_LENGTH, i18n._('Name must be at most {max} characters', { max: SPOOL_NAME_MAX_LENGTH }))
    .required(i18n._('An inspection point name is required')),
  spoolId: string().required(i18n._('A spool is required')),
  projectId: string().required(i18n._('A project Id name is required')),
});

interface InspectionPointsFormProps {
  projectId: string;
  activeSpoolId: string;
  activeSpoolLabel: string;
  activeSpoolName?: string;
  canEditSpools: boolean;
  mutation: UseMutationResult<string, unknown, InspectionPointMutation, unknown>;
  onSubmit: (values: InspectionPointMutation) => Promise<void>;
  resetSpool: () => void;
}

const InspectionPointsForm = ({
  projectId,
  activeSpoolId,
  activeSpoolLabel,
  activeSpoolName,
  canEditSpools,
  mutation,
  onSubmit,
  resetSpool,
}: InspectionPointsFormProps) => {
  const {
    formik,
    helpers,
  } = useForm<Weaken<InspectionPointMutation>>({
    mutation,
    formikConfig: {
      initialValues: {
        projectId,
      },
      onSubmit: async (values) => {
        try {
          await onSubmit(values as InspectionPointMutation);
        } catch {
          // The spool rename failed and has been reported; keep the form so it can be retried.
          return;
        }
        resetSpool();
      },
      validationSchema: InspectionPointSchema,
    },
  });

  const { values, handleBlur, handleChange, handleSubmit, resetForm } = formik;
  useEffect(() => {
    resetForm({
      values: {
        projectId,
        spoolId: activeSpoolId,
        name: activeSpoolName ?? '',
      },
    });
  }, [activeSpoolId, activeSpoolName, resetForm, projectId]);

  return (
    <form onSubmit={handleSubmit}>
      <Box
        component={Paper}
        sx={{
          px: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant="body1">
            <Trans id="Add Inspection Point">Add Inspection Point</Trans>
          </Typography>

          <IconButton
            onClick={resetSpool}
            edge="end"
            color="inherit"
            aria-label="close"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <FormSpacing>
          <TextField
            fullWidth
            id="spool"
            name="spool"
            label={i18n._('Spool')}
            value={activeSpoolLabel}
            size="small"
            InputLabelProps={{ shrink: true }}
            slotProps={{
              input: { readOnly: true },
            }}
          />

          <TextField
            required
            fullWidth
            id="name"
            name="name"
            label={canEditSpools ? i18n._('Spool Name') : i18n._('Inspection Point Name')}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={helpers.hasError('name')}
            helperText={helpers.getErrorHelpText('name') || (canEditSpools ? i18n._('Names the spool; also used as the inspection point label') : undefined)}
            size="small"
          />
        </FormSpacing>

        <Box
          sx={{
            py: 2,
            display: 'flex',
            justifyContent: 'right',
          }}
        >
          <FormikActions
            formik={formik}
            mutation={mutation}
            submitText={i18n._('Submit')}
            right={['submit']}
          />
        </Box>
      </Box>
    </form>
  );
};

export default InspectionPointsForm;
